import { useState } from "react";
import { toast } from "sonner";
import Nav from "@/components/sections/Nav";
import Pricing from "@/components/sections/Pricing";
import Footer from "@/components/sections/Footer";
import { FadeIn } from "@/components/FadeIn";
import { createCheckout } from "@/lib/api";
import { usePageMeta } from "@/hooks/usePageMeta";

/**
 * Ratgeber-/Guide-Landingpage.
 * Für Top-Funnel-Content wie „Trauerrede schreiben", „Wie lang", „Einleitung", „Halten".
 */
export default function GuidePage({
  slug,
  metaTitle,
  metaDescription,
  eyebrow,
  headline,
  intro,
  chapters,        // Array of { title, body (JSX) }
  faq,             // Array of { q, a }
  related,         // Array of { label, to }
  ctaLabel = "Entwurf erstellen",
}) {
  const canonical = `https://oratiorede.de/${slug}`;
  usePageMeta({
    title: metaTitle,
    description: metaDescription,
    canonical,
    ogImage: "https://oratiorede.de/og-image.jpg",
  });

  const [loading, setLoading] = useState(false);
  const [loadingPackage, setLoadingPackage] = useState(null);

  const scrollToPricing = () => {
    document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" });
  };

  const handleCheckout = async (packageId = "oratio_draft") => {
    setLoading(true);
    setLoadingPackage(packageId);
    try {
      const data = await createCheckout(window.location.origin, packageId);
      window.location.href = data.url;
    } catch (e) {
      console.error(e);
      toast.error("Zahlung konnte nicht gestartet werden. Bitte später erneut versuchen.");
      setLoading(false);
      setLoadingPackage(null);
    }
  };

  const faqLd = faq && faq.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faq.map(({ q, a }) => ({
      "@type": "Question",
      "name": q,
      "acceptedAnswer": { "@type": "Answer", "text": a },
    })),
  } : null;

  return (
    <div className="bg-cream min-h-screen">
      <Nav onCta={scrollToPricing} />

      {/* Hero */}
      <section className="pt-40 pb-16 md:pt-52 md:pb-20">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <FadeIn>
            <p className="label-eyebrow mb-8" data-testid={`guide-eyebrow-${slug}`}>{eyebrow}</p>
          </FadeIn>
          <FadeIn delay={0.1}>
            <h1
              data-testid={`guide-h1-${slug}`}
              className="font-serif text-4xl sm:text-5xl md:text-6xl leading-[1.1] tracking-tight text-charcoal max-w-3xl"
            >
              {headline}
            </h1>
          </FadeIn>
          <FadeIn delay={0.2}>
            <p className="mt-10 text-lg md:text-xl leading-relaxed text-ink max-w-2xl font-serif">
              {intro}
            </p>
          </FadeIn>
          <FadeIn delay={0.3}>
            <div className="mt-12 flex flex-col sm:flex-row gap-4">
              <button onClick={scrollToPricing} data-testid={`guide-cta-${slug}`} className="btn-primary">
                {ctaLabel}
              </button>
              <a href="/" className="btn-secondary">Zur Startseite</a>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Chapters */}
      {chapters && chapters.length > 0 && (
        <section className="py-16 md:py-24 border-t border-umber/10">
          <div className="max-w-3xl mx-auto px-6 md:px-12">
            {chapters.map((ch, i) => (
              <FadeIn key={i} delay={0.03 * i}>
                <article className={`${i > 0 ? "mt-16 md:mt-20 pt-16 md:pt-20 border-t border-umber/10" : ""}`}>
                  <p className="font-display tracking-[0.28em] text-[11px] uppercase text-umber mb-4">
                    {String(i + 1).padStart(2, "0")}
                  </p>
                  <h2 className="font-serif text-3xl md:text-4xl text-charcoal leading-tight">
                    {ch.title}
                  </h2>
                  <div className="mt-6 font-serif text-lg text-charcoal leading-[1.75] space-y-5">
                    {ch.body}
                  </div>
                </article>
              </FadeIn>
            ))}
          </div>
        </section>
      )}

      {/* FAQ */}
      {faq && faq.length > 0 && (
        <section className="py-20 md:py-28 border-t border-umber/10 bg-paper/40">
          <div className="max-w-3xl mx-auto px-6 md:px-12">
            <FadeIn>
              <p className="label-eyebrow mb-6">Häufige Fragen</p>
              <h2 className="font-serif text-3xl md:text-4xl text-charcoal leading-tight">
                Was uns oft gefragt wird
              </h2>
            </FadeIn>
            <div className="mt-14 space-y-10">
              {faq.map(({ q, a }, i) => (
                <FadeIn key={i} delay={0.04 * i}>
                  <div className="border-b border-umber/10 pb-8 last:border-0">
                    <h3 className="font-serif text-xl text-charcoal leading-snug">{q}</h3>
                    <p className="mt-3 font-serif text-base text-ink leading-relaxed">{a}</p>
                  </div>
                </FadeIn>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Pricing */}
      <Pricing onCta={handleCheckout} loading={loading} loadingPackage={loadingPackage} />

      {/* Related */}
      {related && related.length > 0 && (
        <section className="pb-20 border-t border-umber/10 pt-16">
          <div className="max-w-4xl mx-auto px-6 md:px-12">
            <p className="label-eyebrow mb-6 text-center">Weiterlesen</p>
            <div className="flex flex-wrap justify-center gap-3">
              {related.map(({ label, to }) => (
                <a key={to} href={to} className="chip" data-testid={`guide-related-${to.replace("/", "")}`}>
                  {label}
                </a>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />

      {faqLd && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqLd) }}
        />
      )}
    </div>
  );
}
